import type { UseSnackHook } from '../hooks/useSnack';
import type { UseExceptionHook } from '../hooks/useException';
import type { Dispatch, SetStateAction } from 'react';
import type { JobResult } from '@erikdakoda/execution/types';

export type DoFetchParams = {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params: Record<string, unknown>;
  setIsLoading?: Dispatch<SetStateAction<boolean>>;
  setLoadingRows?: Dispatch<SetStateAction<Record<string, boolean>>>;
  rowId?: string;
  snack?: UseSnackHook['snack'];
  exception?: UseExceptionHook;
  cache?: RequestCache;
};

export async function doFetch<T>({
  rowId,
  url,
  method = 'GET',
  params,
  setLoadingRows,
  setIsLoading,
  snack,
  exception,
  cache = 'no-store',
}: DoFetchParams): Promise<T> {
  setLoadingRows?.((lr) => ({ ...lr, [rowId ?? '']: true }));
  setIsLoading?.(true);
  let result: unknown;
  try {
    if (method === 'GET') {
      const searchParams = new URLSearchParams(params as Record<string, string>);
      url = `${url}?${searchParams.toString()}`;
    }
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: method === 'POST' ? JSON.stringify(params) : undefined,
      cache,
    });

    result = await response.json();
    if (response.ok) {
      const { success, error } = result as JobResult<unknown>;
      if (success) {
        snack?.success(success);
      } else if (error) {
        exception?.(error);
      }
    } else {
      const { error } = result as JobResult<unknown>;
      throw new Error(error);
    }
  } catch (err: unknown) {
    exception?.(err);
  } finally {
    setLoadingRows?.((lr) => {
      const { [rowId ?? '']: _, ...rest } = lr;
      return rest;
    });
    setIsLoading?.(false);
  }

  return result as T;
}
